import { CustomerVisit } from '../customer-visit';

export enum CustomerVisitStatus {
  NoBid = 0,
  Bid = 1,
  Sold = 2,
  Running = 3,
  RescheduleNeeded = 4,
  RevisitNeeded = 5,
  Pending = 6,
  AvailableForInsideSales = 7,
  HotQuote = 8,
  PendingDeadAppointment = 9,
  DeadAppointment = 10,
  Revisit = 11,
  AlreadySold = 12,
  Rescheduled = 13,
}

const generalActiveStatuses = new Set<number>([
  CustomerVisitStatus.Bid,
  CustomerVisitStatus.Running,
  CustomerVisitStatus.Pending,
  CustomerVisitStatus.HotQuote,
  CustomerVisitStatus.Revisit,
  CustomerVisitStatus.Rescheduled,
]);

const softClosedStatuses = new Set<number>([
  CustomerVisitStatus.RescheduleNeeded,
  CustomerVisitStatus.RevisitNeeded,
  CustomerVisitStatus.AvailableForInsideSales,
  CustomerVisitStatus.PendingDeadAppointment,
]);

const hardClosedStatuses = new Set<number>([
  CustomerVisitStatus.NoBid,
  CustomerVisitStatus.Sold,
  CustomerVisitStatus.DeadAppointment,
  CustomerVisitStatus.AlreadySold,
]);

export interface CustomerVisitResolution {
  allVisits: CustomerVisit[];
  activeVisits: CustomerVisit[];
  selectedVisit?: CustomerVisit;
  hasMultipleActiveVisits: boolean;
}

export function isGeneralActiveCustomerVisit(status: number): boolean {
  return generalActiveStatuses.has(status);
}

export function isSoftClosedCustomerVisit(status: number): boolean {
  return softClosedStatuses.has(status);
}

export function isHardClosedCustomerVisit(status: number): boolean {
  return hardClosedStatuses.has(status);
}

export function compareCustomerVisitsByEffectiveRecency(
  left: CustomerVisit,
  right: CustomerVisit
): number {
  const effectiveDateDifference =
    getDateValue(right.dispositionDate ?? right.createdDate) -
    getDateValue(left.dispositionDate ?? left.createdDate);

  if (effectiveDateDifference !== 0) {
    return effectiveDateDifference;
  }

  return getDateValue(right.createdDate) - getDateValue(left.createdDate);
}

export function sortCustomerVisitsByEffectiveRecency(
  customerVisits: CustomerVisit[]
): CustomerVisit[] {
  return [...customerVisits].sort(compareCustomerVisitsByEffectiveRecency);
}

export function resolveCustomerVisits(
  customerVisits: CustomerVisit[]
): CustomerVisitResolution {
  const allVisits = sortCustomerVisitsByEffectiveRecency(customerVisits);
  const activeVisits = allVisits.filter((visit) =>
    isGeneralActiveCustomerVisit(visit.status)
  );

  return {
    allVisits,
    activeVisits,
    selectedVisit: activeVisits[0],
    hasMultipleActiveVisits: activeVisits.length > 1,
  };
}

function getDateValue(value: Date | string | undefined): number {
  if (!value) {
    return 0;
  }

  const dateValue = new Date(value).getTime();
  return Number.isNaN(dateValue) ? 0 : dateValue;
}
